type Encounter = Record<string, unknown>;

/** Preserve the newest full adoption; a summary can still paint the combat strip before it arrives. */
export function chooseFt3dEncounterArr(candidates: readonly (Encounter | null | undefined)[],
    matches: (candidate: Encounter) => boolean): Encounter | null {
    let summary: Encounter | null = null;
    for (const candidate of candidates) {
        if (!candidate || !matches(candidate)) continue;
        if (Array.isArray(candidate.encounter_creatures_arr)) return candidate;
        summary ??= candidate;
    }
    return summary;
}

/** A parked adoption must release the shell's loading state, even if the transport never settles. */
export async function awaitFt3dRoster(read: Promise<unknown>, timeoutMs = 20_000): Promise<unknown> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([read, new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('encounter-roster-load-timeout')), timeoutMs);
        })]);
    } finally { clearTimeout(timer); }
}

export function showFt3dRosterStatus(frame: HTMLElement | null, roster: HTMLElement | null,
    state: 'loading' | 'error' | 'ready', retry: () => void): void {
    if (!frame || !roster) return;
    let status = document.querySelector<HTMLElement>('[data-ft3d-roster-status]');
    roster.hidden = state !== 'ready';
    if (state === 'ready') { if (status) status.hidden = true; return; }
    if (!status) {
        status = document.createElement('div');
        status.setAttribute('data-ft3d-roster-status', '');
        status.setAttribute('role', 'status');
        status.className = 'p-2';
        roster.before(status);
    }
    // A failed scene keeps the loading chrome hidden. Its recovery control must remain reachable.
    if (state === 'error') {
        if (status.parentElement !== document.body) document.body.append(status);
        Object.assign(status.style, { position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
            zIndex: '2147483000', maxWidth: '90vw', background: '#301b1b', color: '#fff', border: '1px solid #b66', borderRadius: '.4rem' });
    } else {
        if (status.nextSibling !== roster) roster.before(status);
        status.style.cssText = '';
    }
    status.hidden = false;
    if (status.dataset.state === state) return;
    status.dataset.state = state;
    status.textContent = state === 'error'
        ? 'The encounter roster could not be loaded. Character locations and controls are unavailable. '
        : 'Loading encounter roster…';
    if (state === 'error') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-sm btn-outline-light';
        button.textContent = 'Retry';
        button.setAttribute('data-ft3d-roster-retry', '');
        button.addEventListener('click', retry);
        status.append(button);
    }
}

declare global {
    interface Window {
        ftChooseFt3dEncounterArr: typeof chooseFt3dEncounterArr;
        ftAwaitFt3dRoster: typeof awaitFt3dRoster;
        ftShowFt3dRosterStatus: typeof showFt3dRosterStatus;
    }
}
Object.assign(window, { ftChooseFt3dEncounterArr: chooseFt3dEncounterArr,
    ftAwaitFt3dRoster: awaitFt3dRoster, ftShowFt3dRosterStatus: showFt3dRosterStatus });
